import { type KitText, KitTextProvider } from "@kopi/ui/components/kit-text";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { browserStorage } from "@/lib/storage";
import { type Area, areaMessages } from "./area";
import { type Formatter, formatterFor } from "./format";
import { INTL_LOCALE, type Locale, writeLocale } from "./locale";
import { id, type MessageKey, type Messages } from "./messages/id";
import { type Dictionary, type MessageParams, type TranslateOf, translate } from "./translate";

/** `t` over the core dictionary (shell, register, kit). */
export type Translate = TranslateOf<typeof id>;

/** `t` over an area's dictionary and the core's, as `useMessages(area)` returns it. */
export type AreaTranslate<D extends Dictionary> = TranslateOf<typeof id & D>;

interface I18nState {
  locale: Locale;
  t: Translate;
  /** The core dictionary in the active language, for `useMessages`. */
  messages: Messages;
  /** Numbers, money and dates in the active language. */
  format: Formatter;
  /**
   * Loads the language (English is its own chunk), then switches the whole app to it. When the chunk
   * can't be fetched (offline) the app stays in its current language and this resolves `false`.
   */
  setLocale: (locale: Locale) => Promise<boolean>;
}

const I18nContext = createContext<I18nState | null>(null);

export function useI18n(): I18nState {
  const state = useContext(I18nContext);
  if (!state) throw new Error("useI18n must be used inside <I18nProvider>");
  return state;
}

/**
 * `useI18n()` for a screen with its own area dictionary: `t` reads the area's keys and the core's.
 * `useMessages(catalogue).t("catalogue.title")`.
 */
export function useMessages<D extends Dictionary>(
  area: Area<D>,
): Omit<I18nState, "t"> & { t: AreaTranslate<D> } {
  const state = useI18n();
  const { locale, messages } = state;
  const t = useMemo(() => {
    const merged = areaMessages(messages, area, locale);
    const intl = INTL_LOCALE[locale];
    return ((key: string, params?: MessageParams) =>
      translate(merged, key, params, intl)) as AreaTranslate<D>;
  }, [messages, area, locale]);
  return { ...state, t };
}

/** Indonesian ships with the shell; English is downloaded only when it's the chosen language. */
export async function loadMessages(locale: Locale): Promise<Messages> {
  return locale === "en" ? (await import("./messages/en")).en : id;
}

/**
 * The language to start in, loaded: the chosen one, or Indonesian (already in the bundle) when its chunk
 * can't be fetched, e.g. offline with a stale cache. The app always renders.
 */
export async function loadInitial(locale: Locale): Promise<{ locale: Locale; messages: Messages }> {
  try {
    return { locale, messages: await loadMessages(locale) };
  } catch {
    return { locale: "id", messages: id };
  }
}

/** `<html lang>`, for screen readers, hyphenation and the browser's own translate offer. */
export function applyDocumentLocale(locale: Locale) {
  document.documentElement.lang = locale;
}

/**
 * Owns the interface language (R1-09e). `initial` is loaded before the first render (main.tsx), so the
 * first paint is already in the chosen language; switching loads the other dictionary, then re-renders.
 */
export function I18nProvider(props: {
  initial: { locale: Locale; messages: Messages };
  children: ReactNode;
}) {
  const [current, setCurrent] = useState(props.initial);

  const setLocale = useCallback(async (locale: Locale) => {
    let messages: Messages;
    try {
      messages = await loadMessages(locale);
    } catch {
      return false;
    }
    writeLocale(browserStorage(), locale);
    applyDocumentLocale(locale);
    setCurrent({ locale, messages });
    return true;
  }, []);

  const value = useMemo<I18nState>(() => {
    const { locale, messages } = current;
    const intl = INTL_LOCALE[locale];
    const t = ((key: MessageKey, params?: MessageParams) =>
      translate(messages, key, params, intl)) as Translate;
    return { locale, t, messages, format: formatterFor(locale), setLocale };
  }, [current, setLocale]);

  const kitText = useMemo(() => kitTextFor(value.t), [value.t]);

  return (
    <I18nContext.Provider value={value}>
      <KitTextProvider value={kitText}>{props.children}</KitTextProvider>
    </I18nContext.Provider>
  );
}

/** The kit's own words (Batal, Cari…, Muat lebih banyak…) in the active language. */
function kitTextFor(t: Translate): KitText {
  return {
    cancel: t("common.cancel"),
    close: t("kit.close"),
    retry: t("common.retry"),
    searchPlaceholder: t("kit.searchPlaceholder"),
    search: t("kit.search"),
    noMatch: t("kit.noMatch"),
    all: t("kit.all"),
    optionsFailed: t("kit.optionsFailed"),
    loadFailed: t("kit.loadFailed"),
    loadFailedHint: t("kit.loadFailedHint"),
    noResults: t("kit.noResults"),
    noResultsFor: (query) => t("kit.noResultsFor", { query }),
    empty: t("kit.empty"),
    actions: t("kit.actions"),
    loading: t("kit.loading"),
    loadMore: t("kit.loadMore"),
    nextPageFailed: t("kit.nextPageFailed"),
    pagination: t("kit.pagination"),
    previousPage: t("kit.previousPage"),
    nextPage: t("kit.nextPage"),
    pageNumber: (page) => t("kit.pageNumber", { page }),
    pickDate: t("kit.pickDate"),
    clearDate: t("kit.clearDate"),
    sortBy: t("kit.sortBy"),
    sortAscending: t("kit.sortAscending"),
    sortDescending: t("kit.sortDescending"),
    breadcrumb: t("kit.breadcrumb"),
    more: t("kit.more"),
  };
}
